import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DISPENSING_LOG_PATHS, IP_PATHS } from "Routes/paths";

interface GlobalStudyMismatchWarningProps {
  globalStudy: { studyid?: number | string; studyname?: string; studycode?: string } | null | undefined;
  studyOptions: { value: number | string; label: string }[];
  isLoaded?: boolean;
  message?: string;
  isPresent?: boolean;
  isreturned?: boolean;
}

const GlobalStudyMismatchWarning: React.FC<GlobalStudyMismatchWarningProps> = ({
  globalStudy,
  studyOptions,
  isLoaded = true,
  message,
  isPresent: isPresentOverride,
  isreturned = false,
}) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (!isLoaded || !globalStudy?.studyid || studyOptions.length === 0) return null;

  const isPresent =
    isPresentOverride !== undefined
      ? isPresentOverride
      : studyOptions.some((opt) => String(opt.value) === String(globalStudy.studyid));

  if (isPresent) return null;

  const studyLabel =
    globalStudy.studycode ||
    globalStudy.studyname ||
    `Study ID: ${globalStudy.studyid}`;

  const defaultMessage = isreturned
    ? `The selected study "${studyLabel}" does not have any Dispensed inventory records. Please dispense Investigational Products to continue.`
    : `The selected study "${studyLabel}" does not have any IP-verified inventory records. Please add Investigational Products to continue.`;

  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "24px",
        padding: "16px 20px",
        marginBottom: "20px",
        borderRadius: "12px",
        background: "#fffbeb",
        border: "1px dashed #fcd34d",
        boxShadow: "0 1px 6px rgba(217, 119, 6, 0.05)",
        position: "relative",
      }}
    >
      {/* Box + Warning icon with Sparks */}
      <div style={{ flexShrink: 0, position: "relative", width: "54px", height: "50px" }}>
        {/* Sparks */}
        <svg
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}
          viewBox="0 0 54 50"
        >
          {/* Top Sparks */}
          <line x1="27" y1="2" x2="27" y2="6" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
          <line x1="18" y1="4" x2="21" y2="8" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="36" y1="4" x2="33" y2="8" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
          
          {/* Left Sparks */}
          <line x1="6" y1="18" x2="11" y2="20" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="4" y1="26" x2="9" y2="26" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="6" y1="34" x2="11" y2="32" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
          
          {/* Right Sparks */}
          <line x1="48" y1="18" x2="43" y2="20" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="50" y1="26" x2="45" y2="26" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
        </svg>

        {/* Box Icon (Centered slightly lower) */}
        <div style={{ position: "absolute", top: "8px", left: "7px" }}>
          <i
            className="ri-archive-2-fill"
            style={{ fontSize: "40px", color: "#f59e0b", lineHeight: 1 }}
          />
        </div>

        {/* Warning Triangle Overlay */}
        <div
          style={{
            position: "absolute",
            bottom: "-1px",
            right: "3px",
            background: "#fffbeb",
            width: "22px",
            height: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
          }}
        >
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none">
            <path
              d="M10 1L1 17H19L10 1Z"
              fill="#d97706"
              stroke="#d97706"
              strokeWidth="1"
              strokeLinejoin="round"
            />
            {/* Exclamation point inside warning triangle */}
            <rect x="9.25" y="6" width="1.5" height="5" rx="0.75" fill="white" />
            <circle cx="10" cy="14" r="1" fill="white" />
          </svg>
        </div>
      </div>

      {/* Text content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#78350f", marginBottom: "4px" }}>
        {isreturned ? "Inventory not dispensed" : "Inventory not added"} for this study
        </p>
        <p style={{ margin: 0, fontSize: "13px", color: "#92400e", lineHeight: "1.6" }}>
          {message ?? defaultMessage}
        </p>
      </div>

      {/* Add Inventory button */}
      <button
        onClick={() => (isreturned ? navigate(DISPENSING_LOG_PATHS.ADD) : navigate(IP_PATHS.ADD))}
        style={{
          flexShrink: 0,
          padding: "8px 20px",
          fontSize: "13px",
          fontWeight: 600,
          color: "#fff",
          background: "#f59e0b",
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: "0 2px 4px rgba(245, 158, 11, 0.2)",
          transition: "background 0.15s ease, transform 0.1s ease",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = "#d97706";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = "#f59e0b";
        }}
      >
       {isreturned ? "Dispense Inventory" : "Add Inventory"}
      </button>

    </div>
  );
};

export default GlobalStudyMismatchWarning;
